import ScatterPlotIcon from "@/components/icons/ScatterPlotIcon";
import List, { ListDataItem } from "@/components/visualizations/List/List";
import useTranslation from "@/hooks/useTranslation";
import { facilityIcon } from "@/pages/dashboard/FacilityDashboard/widgets/FacilityImpactTopContributors";
import { transportIcon } from "@/pages/dashboard/TransportDashboard/widgets/TransportImpactTopContributors";
import { useCompanyImpactTopContributors } from "@/pages/dashboard/hooks/useCompanyImpact";
import { useDateRange } from "@/pages/dashboard/hooks/useDateRange";
import { formatTransportLabel } from "@/pages/dashboard/utils/formatters";
import { ImpactMetric } from "@/pages/dashboard/utils/types";
import { useEffect, useState } from "react";
import Widget from "../../components/Widget/Widget";
import PackageIcon from "@/components/icons/chapters/PackageIcon";

const step = 10;

export default function CorporateImpactTopContributors({ metric }: { metric: ImpactMetric }) {
  const [limit, setLimit] = useState(step);
  const { startDate, endDate } = useDateRange();
  const translation = useTranslation();

  useEffect(() => {
    setLimit(step);
  }, [startDate, endDate]);

  const { data, isLoading, isFetching } = useCompanyImpactTopContributors({
    page: 1,
    pageSize: limit + step,
    activeSorting: {
      column: metric.key,
      order: "descend",
    },
    metric: metric.key,
  });

  const numMore = Math.min((data?.contributors.length || 0) - limit);

  const listData: ListDataItem[] = data
    ? data?.contributors.slice(0, limit).map((row) => {
        const item: ListDataItem = {
          icon: undefined,
          label: row.label,
          value: row.impact[metric.key],
          extraProp: { label: "Segment", value: row.type },
        };

        if (row.type === "Transport") {
          item.icon = transportIcon(row.label);
          item.label = formatTransportLabel(row.label);
        }

        if (row.type === "Facility") {
          item.icon = facilityIcon(row.subtype);
        }

        if (row.type === "Material") {
          item.icon = <ScatterPlotIcon />;
        }

        if (row.type === "Packaging") {
          item.icon = <PackageIcon />;
        }

        return item;
      })
    : [];

  return (
    <Widget
      title={"Top contributors"}
      tooltip={translation["dashboard"]["company"]["top_contributors"]}
    >
      <List
        loading={isLoading || isFetching}
        data={{
          list: listData,
          total: data?.company_impact[metric.key],
        }}
        loadMore={{
          show: numMore > 0,
          numberOfMore: numMore,
          fn: () => setLimit(limit + step),
        }}
        format={metric.formatter}
      />
    </Widget>
  );
}
